import { useMemo, useCallback } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { omitKeys } from 'helper/omitKeys';

export const useBreakpointFlags = () => {
  const theme = useTheme();
  const { breakpoints } = theme;

  const isXs = useMediaQuery(breakpoints.only('xs'));
  const isSm = useMediaQuery(breakpoints.only('sm'));
  const isMd = useMediaQuery(breakpoints.only('md'));
  const isLg = useMediaQuery(breakpoints.only('lg'));
  const isXl = useMediaQuery(breakpoints.only('xl'));
  const isXxl = useMediaQuery(breakpoints.only('xxl'));

  const isXsUp = useMediaQuery(breakpoints.up('xs')); // Always true
  const isSmUp = useMediaQuery(breakpoints.up('sm'));
  const isMdUp = useMediaQuery(breakpoints.up('md'));
  const isLgUp = useMediaQuery(breakpoints.up('lg'));
  const isXlUp = useMediaQuery(breakpoints.up('xl'));
  const isXxlUp = useMediaQuery(breakpoints.up('xxl'));

  const isXsDown = useMediaQuery(breakpoints.down('xs'));
  const isSmDown = useMediaQuery(breakpoints.down('sm'));
  const isMdDown = useMediaQuery(breakpoints.down('md'));
  const isLgDown = useMediaQuery(breakpoints.down('lg'));
  const isXlDown = useMediaQuery(breakpoints.down('xl'));
  const isXxlDown = useMediaQuery(breakpoints.down('xxl')); // Always true

  const currentBreakpoint = useMemo(() => {
    const { keys } = breakpoints;
    const matches = {
      xs: isXs,
      sm: isSm,
      md: isMd,
      lg: isLg,
      xl: isXl,
      xxl: isXxl,
    };

    return keys.find((key) => matches[key]) || keys[keys.length - 1];
  }, [breakpoints, isXs, isSm, isMd, isLg, isXl, isXxl]);

  const getResponsiveValueForCurrent = useCallback(
    (values) => {
      if (values[currentBreakpoint]) {
        return values[currentBreakpoint];
      }

      if (currentBreakpoint === 'xxl' && values.xl) {
        return values.xl;
      }

      return values.md || values.xs || Object.values(values)[0];
    },
    [currentBreakpoint],
  );

  const getResponsiveTypography = useCallback(
    (variant) => {
      const variantStyles = theme.typography[variant] || {};
      const mdUpKey = breakpoints.up('md');
      const baseStyles = omitKeys(variantStyles, mdUpKey);

      return isMdUp ? { ...baseStyles, ...variantStyles[mdUpKey] } : baseStyles;
    },
    [theme.typography, breakpoints, isMdUp],
  );

  return {
    // Basic flags
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    isXxl,

    // Up flags
    isXsUp,
    isSmUp,
    isMdUp,
    isLgUp,
    isXlUp,
    isXxlUp,

    // Down flags
    isXsDown,
    isSmDown,
    isMdDown,
    isLgDown,
    isXlDown,
    isXxlDown,

    // Utilities
    currentBreakpoint,
    getResponsiveValueForCurrent,
    getResponsiveTypography,
  };
};
